import { useTranslation } from '@lyearn/i18n';
import { LyearnPlus, PointsOutline } from '@lyearn/icons';
import { Input, Stack, Typography } from '@lyearn/ui';
import RadioGroup from '@/components/RadioGroup';
import { PointsConfigInput, PointsConfigTypeEnum } from '@/types/schema';

export enum PointsConfigType {
  PredefinedPoints = 'PREDEFINED_POINTS',
  CustomPoints = 'CUSTOM_POINTS',
}

export const PointsConfig = ({
  value,
  onChange,
}: {
  value?: PointsConfigInput;
  onChange: (value: PointsConfigInput) => void;
}) => {
  const { t } = useTranslation(['Course', 'Gamification']);

  const options = [
    {
      label: t('Course:Form.PredefinedPoints'),
      id: PointsConfigTypeEnum.Default,
      Icon: () => (
        <Stack className="flex-shrink-0 mr-4">
          <LyearnPlus fill="transparent" />
        </Stack>
      ),
    },
    {
      label: t('Course:Form.CustomPoints'),
      id: PointsConfigTypeEnum.Custom,
      Icon: () => (
        <Stack className="flex-shrink-0 mr-4">
          <PointsOutline />
        </Stack>
      ),
    },
  ];

  const _onChange = (optionId: PointsConfigTypeEnum) => {
    if (optionId === PointsConfigTypeEnum.Default) {
      onChange({ points: 10, type: PointsConfigTypeEnum.Default });
    } else {
      onChange({ points: 10, type: PointsConfigTypeEnum.Custom });
    }
  };

  return (
    <Stack direction="col" gapY={32}>
      <Stack className="pl-8" direction="col" gapY={8}>
        <Typography variant="heading-s">{t('Course:Form.PointsConfigTitle')}</Typography>
        <Typography color="text-text-secondary" variant="body-short-01">
          {t('Course:Form.PointsConfigDescription')}
        </Typography>
      </Stack>
      <RadioGroup<PointsConfigTypeEnum>
        className="w-full grid grid-cols-2"
        direction="row"
        gap={12}
        options={options}
        selectedId={value?.type}
        size="small"
        type="box"
        onChange={_onChange}
      />
      {value?.type === PointsConfigTypeEnum.Custom && (
        <Stack direction="col" gapY={8}>
          <Typography className="pl-8" variant="body-short-02">
            {t('Gamification:Points')}
          </Typography>
          <Input
            size="large"
            type="number"
            value={value?.points ?? ''}
            onChange={(e) => {
              const num = parseInt(e.target.value.replace(/[^\d]/g, ''), 10);
              onChange({ type: PointsConfigTypeEnum.Custom, points: isNaN(num) ? 0 : num });
            }}
            onKeyDown={(e) => {
              // Prevent non-numeric input (except for backspace, delete, arrows)
              if (
                !/[\d]/.test(e.key) &&
                !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
